import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Card, Empty } from "@/components/Bits";
import { Building2, Link2, RefreshCw, Trash2, Plus, X, CheckCircle2, AlertCircle, ShieldCheck, Upload, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const BROKER_INFO = {
  schwab: { name: "Charles Schwab", subtitle: "OAuth 2.0 · Auto-sync trades from your Schwab brokerage account" },
  ibkr: { name: "Interactive Brokers", subtitle: "Flex Web Service · Paste your Flex Token + Query ID" },
  webull: { name: "Webull", subtitle: "CSV export from app · No live API for retail" },
};

export default function Connections() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [showIbkr, setShowIbkr] = useState(false);
  const [ibkrForm, setIbkrForm] = useState({ flex_token: "", query_id: "", label: "Main IBKR" });

  useEffect(() => {
    if (params.get("schwab") === "connected") {
      toast.success("SCHWAB CONNECTED");
      qc.invalidateQueries({ queryKey: ["broker-connections"] });
    } else if (params.get("schwab_error")) {
      toast.error(`Schwab error: ${params.get("schwab_error")}`);
    }
  }, [params, qc]);

  const { data, isLoading } = useQuery({
    queryKey: ["broker-connections"],
    queryFn: async () => (await api.get("/brokers/connections")).data,
  });

  const schwabConnectMut = useMutation({
    mutationFn: async () => (await api.get("/brokers/schwab/connect")).data,
    onSuccess: (d) => { window.location.href = d.auth_url; },
    onError: (e) => toast.error(e?.response?.data?.detail || "Schwab not configured yet"),
  });

  const syncMut = useMutation({
    mutationFn: async (broker) => (await api.post(`/brokers/${broker}/sync`)).data,
    onSuccess: (d) => {
      toast.success(`${d.broker.toUpperCase()} SYNCED · ${d.inserted} trades`);
      qc.invalidateQueries({ queryKey: ["broker-connections"] });
      qc.invalidateQueries({ queryKey: ["trades"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Sync failed"),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => api.delete(`/brokers/connections/${id}`),
    onSuccess: () => {
      toast.success("CONNECTION REMOVED");
      qc.invalidateQueries({ queryKey: ["broker-connections"] });
    },
  });

  const ibkrConnectMut = useMutation({
    mutationFn: async () => (await api.post("/brokers/ibkr/connect", ibkrForm)).data,
    onSuccess: () => {
      toast.success("IBKR CONNECTED · Click SYNC to pull your trades");
      qc.invalidateQueries({ queryKey: ["broker-connections"] });
      setShowIbkr(false);
      setIbkrForm({ flex_token: "", query_id: "", label: "Main IBKR" });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Connect failed"),
  });

  const connections = data?.connections || [];
  const schwabConfigured = data?.schwab_configured;
  const schwabConn = connections.find((c) => c.broker === "schwab");
  const ibkrConn = connections.find((c) => c.broker === "ibkr");

  return (
    <div>
      <PageHeader eyebrow="DATA INGRESS" title="Broker Connections" />

      <div className="p-8 grid lg:grid-cols-2 gap-6">
        {/* Schwab */}
        <Card eyebrow="OAUTH 2.0" title={BROKER_INFO.schwab.name} action={
          schwabConn ? (
            <span className="inline-flex items-center gap-1 text-xs text-[color:var(--accent)] font-mono-num">
              <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} /> CONNECTED
            </span>
          ) : null
        }>
          <p className="text-xs text-[color:var(--fg-3)] mb-4">{BROKER_INFO.schwab.subtitle}</p>

          {!schwabConfigured && (
            <div className="border border-amber-500/30 bg-amber-500/5 p-3 mb-4 text-xs text-amber-400 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <div className="font-mono-num">SCHWAB DEVELOPER APP NOT CONFIGURED</div>
                <div className="text-[color:var(--fg-2)] mt-1">
                  Add <code>SCHWAB_CLIENT_ID</code> + <code>SCHWAB_CLIENT_SECRET</code> to backend <code>.env</code> after registering an app at developer.schwab.com.
                </div>
              </div>
            </div>
          )}

          {schwabConn ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-xs font-mono-num">
                <div><div className="label-eyebrow">LAST SYNC</div><div className="text-[color:var(--fg-2)] mt-1">{schwabConn.last_sync_at?.slice(0, 16).replace("T", " ") || "—"}</div></div>
                <div><div className="label-eyebrow">LAST PULLED</div><div className="text-[color:var(--fg-2)] mt-1">{schwabConn.last_sync_count} trades</div></div>
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => syncMut.mutate("schwab")} disabled={syncMut.isPending}
                  data-testid="sync-schwab-btn"
                  className="flex items-center gap-2 bg-[color:var(--accent)] text-black hover:bg-[color:var(--accent)] transition-colors px-3 py-1.5 text-xs disabled:opacity-50">
                  <RefreshCw className={`h-3 w-3 ${syncMut.isPending ? "animate-spin" : ""}`} strokeWidth={1.5} /> SYNC NOW
                </button>
                <button onClick={() => deleteMut.mutate(schwabConn.id)} data-testid="disconnect-schwab-btn"
                  className="flex items-center gap-2 bg-[color:var(--surface)] border border-[color:var(--border)] text-[color:var(--fg-2)] hover:text-rose-400 hover:border-rose-500/40 transition-colors px-3 py-1.5 text-xs">
                  <Trash2 className="h-3 w-3" strokeWidth={1.5} /> DISCONNECT
                </button>
              </div>
            </div>
          ) : (
            <button onClick={() => schwabConnectMut.mutate()} disabled={!schwabConfigured || schwabConnectMut.isPending}
              data-testid="connect-schwab-btn"
              className="flex items-center gap-2 bg-[color:var(--accent)] text-black hover:bg-[color:var(--accent)] transition-colors px-4 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
              <Link2 className="h-4 w-4" strokeWidth={1.5} /> CONNECT SCHWAB
            </button>
          )}
        </Card>

        {/* IBKR */}
        <Card eyebrow="FLEX TOKEN" title={BROKER_INFO.ibkr.name} action={
          ibkrConn ? (
            <span className="inline-flex items-center gap-1 text-xs text-[color:var(--accent)] font-mono-num">
              <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} /> CONNECTED
            </span>
          ) : null
        }>
          <p className="text-xs text-[color:var(--fg-3)] mb-4">{BROKER_INFO.ibkr.subtitle}</p>

          {ibkrConn ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-xs font-mono-num">
                <div><div className="label-eyebrow">QUERY ID</div><div className="text-[color:var(--fg-2)] mt-1">{ibkrConn.query_id}</div></div>
                <div><div className="label-eyebrow">LAST SYNC</div><div className="text-[color:var(--fg-2)] mt-1">{ibkrConn.last_sync_at?.slice(0, 16).replace("T", " ") || "—"}</div></div>
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => syncMut.mutate("ibkr")} disabled={syncMut.isPending}
                  data-testid="sync-ibkr-btn"
                  className="flex items-center gap-2 bg-[color:var(--accent)] text-black hover:bg-[color:var(--accent)] transition-colors px-3 py-1.5 text-xs disabled:opacity-50">
                  <RefreshCw className={`h-3 w-3 ${syncMut.isPending ? "animate-spin" : ""}`} strokeWidth={1.5} /> SYNC NOW
                </button>
                <button onClick={() => deleteMut.mutate(ibkrConn.id)} data-testid="disconnect-ibkr-btn"
                  className="flex items-center gap-2 bg-[color:var(--surface)] border border-[color:var(--border)] text-[color:var(--fg-2)] hover:text-rose-400 hover:border-rose-500/40 transition-colors px-3 py-1.5 text-xs">
                  <Trash2 className="h-3 w-3" strokeWidth={1.5} /> DISCONNECT
                </button>
              </div>
            </div>
          ) : !showIbkr ? (
            <button onClick={() => setShowIbkr(true)} data-testid="open-ibkr-form-btn"
              className="flex items-center gap-2 bg-[color:var(--accent)] text-black hover:bg-[color:var(--accent)] transition-colors px-4 py-2 text-sm">
              <Plus className="h-4 w-4" strokeWidth={1.5} /> ADD IBKR CONNECTION
            </button>
          ) : (
            <div className="space-y-3 border border-[color:var(--border)] p-4">
              <div className="flex items-center justify-between">
                <div className="label-eyebrow">PASTE FROM IBKR CLIENT PORTAL</div>
                <button onClick={() => setShowIbkr(false)} className="text-[color:var(--fg-3)] hover:text-[color:var(--fg)]"><X className="h-3.5 w-3.5" strokeWidth={1.5} /></button>
              </div>
              <div>
                <label className="label-eyebrow block mb-1">LABEL</label>
                <input type="text" value={ibkrForm.label} onChange={(e) => setIbkrForm({ ...ibkrForm, label: e.target.value })}
                  data-testid="ibkr-label" className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] px-2 py-1.5 text-sm focus:outline-none focus:border-[color:var(--accent)] font-mono-num" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">FLEX TOKEN</label>
                <input type="password" value={ibkrForm.flex_token} onChange={(e) => setIbkrForm({ ...ibkrForm, flex_token: e.target.value })}
                  data-testid="ibkr-token" placeholder="e.g. 1234567890abcdef…"
                  className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] px-2 py-1.5 text-sm focus:outline-none focus:border-[color:var(--accent)] font-mono-num" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1">QUERY ID</label>
                <input type="text" value={ibkrForm.query_id} onChange={(e) => setIbkrForm({ ...ibkrForm, query_id: e.target.value })}
                  data-testid="ibkr-query" placeholder="e.g. 123456"
                  className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] px-2 py-1.5 text-sm focus:outline-none focus:border-[color:var(--accent)] font-mono-num" />
              </div>
              <p className="text-xs text-[color:var(--fg-3)] flex items-center gap-2">
                <ShieldCheck className="h-3 w-3 text-[color:var(--accent)]" strokeWidth={1.5} /> Token encrypted at rest with Fernet (server-side VAULT_KEY).
              </p>
              <button onClick={() => ibkrConnectMut.mutate()} disabled={!ibkrForm.flex_token || !ibkrForm.query_id || ibkrConnectMut.isPending}
                data-testid="ibkr-save-btn"
                className="bg-white text-black hover:bg-[color:var(--accent)] transition-colors px-4 py-2 text-sm disabled:opacity-40">
                {ibkrConnectMut.isPending ? "SAVING…" : "SAVE & ENCRYPT"}
              </button>
            </div>
          )}
        </Card>

        <Card eyebrow="WEBULL" title="Webull Account" action={
          <span className="inline-flex items-center gap-1 text-xs text-[color:var(--fg-2)] font-mono-num">
            <Upload className="h-3.5 w-3.5" strokeWidth={1.5} /> CSV EXPORT
          </span>
        }>
          <p className="text-xs text-[color:var(--fg-3)] mb-4">
            Webull doesn&apos;t expose a public live-sync API for retail accounts. Their CSV export is the supported path — our importer auto-detects the Webull column format and rolls fills into clean position-cycle trades.
          </p>
          <div className="space-y-3 mb-4 text-xs font-mono-num text-[color:var(--fg-2)]">
            <div className="border-l-2 border-[color:var(--accent)]/40 pl-3">
              <div className="label-eyebrow text-[color:var(--accent)] mb-1">FROM THE WEBULL DESKTOP APP</div>
              <ol className="space-y-1.5 text-[color:var(--fg-2)] list-decimal list-inside">
                <li>Open Webull → click your profile icon (top-right) → <span className="text-[color:var(--fg)]">Account Details</span></li>
                <li>Click <span className="text-[color:var(--fg)]">Statements &amp; History</span> in the sidebar</li>
                <li>Choose <span className="text-[color:var(--fg)]">Trade History</span> tab → pick a date range</li>
                <li>Click <span className="text-[color:var(--fg)]">Export</span> → download the CSV</li>
              </ol>
            </div>
            <div className="border-l-2 border-[color:var(--accent)]/40 pl-3">
              <div className="label-eyebrow text-[color:var(--accent)] mb-1">FROM THE WEBULL WEB / MOBILE APP</div>
              <ol className="space-y-1.5 text-[color:var(--fg-2)] list-decimal list-inside">
                <li>Profile / Menu → <span className="text-[color:var(--fg)]">Account &amp; Login Security</span> → <span className="text-[color:var(--fg)]">Account Details</span></li>
                <li>Tap <span className="text-[color:var(--fg)]">Statements</span> → <span className="text-[color:var(--fg)]">Trade Confirmations</span></li>
                <li>Select date range → request the document → download as CSV (or share to email)</li>
              </ol>
            </div>
          </div>
          <a href="/import?broker=webull" data-testid="webull-import-link"
            className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black hover:bg-[color:var(--accent)] transition-colors px-4 py-2 text-sm">
            <Upload className="h-4 w-4" strokeWidth={1.5} /> UPLOAD WEBULL CSV <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
          </a>
        </Card>

        <Card eyebrow="ALSO SUPPORTED" title="CSV imports" className="lg:col-span-2">
          <p className="text-xs text-[color:var(--fg-3)]">
            Don&apos;t see your broker? Every broker exports trade data as CSV. We auto-detect IBKR, TD/Schwab, Webull, Robinhood, MetaTrader, Tradovate, and generic formats.
            <a href="/import" className="text-[color:var(--accent)] hover:underline ml-2">Go to CSV Import →</a>
          </p>
        </Card>
      </div>
    </div>
  );
}
